/**
 * UniversalGenerateModal
 * Handles WarmUp, Pool A, Pool B (and optionally Cold Pool) generation with:
 *   - Schema-driven Claude generation
 *   - AdioraSchema v4.3 per-question validation
 *   - Per-question Approve / Discard / Regenerate actions
 *   - Bulk "Approve All Passed" button
 *   - Store only approved + passed questions
 */
import { useState, useEffect, useCallback } from "react";
import { X, CheckCircle, XCircle, RefreshCw, ChevronDown, Zap, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function apiFetch(path, token, opts = {}) {
  return fetch(`${API}/api${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  });
}

// ── Error-type badge ───────────────────────────────────────────────────────

const ET_STYLES = {
  Foundational:       { bg: "#fee2e2", text: "#991b1b" },
  Conceptual:         { bg: "#fed7aa", text: "#9a3412" },
  Procedural:         { bg: "#ede9fe", text: "#5b21b6" },
  Careless:           { bg: "#fef9c3", text: "#854d0e" },
  Overgeneralisation: { bg: "#ccfbf1", text: "#134e4a" },
  Notation:           { bg: "#dbeafe", text: "#1e40af" },
  Mixed:              { bg: "#f3f4f6", text: "#374151" },
};

function ErrorTypeBadge({ type }) {
  if (!type) return null;
  const s = ET_STYLES[type] || { bg: "#f3f4f6", text: "#374151" };
  return (
    <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold ml-1.5"
      style={{ background: s.bg, color: s.text }}>
      {type}
    </span>
  );
}

function DifficultyBadge({ difficulty }) {
  const map = {
    L1_EASY:         { label: "L1 Easy",    bg: "#d1fae5", text: "#065f46" },
    L2_MEDIUM_PROC:  { label: "L2 Medium",  bg: "#dbeafe", text: "#1e40af" },
    L3_MEDIUM_DEPTH: { label: "L3 Depth",   bg: "#ede9fe", text: "#5b21b6" },
    L4_HARD_PROC:    { label: "L4 Hard",    bg: "#fee2e2", text: "#991b1b" },
    L5_HARD_DEPTH:   { label: "L5 Expert",  bg: "#fce7f3", text: "#9d174d" },
    Easy:            { label: "Easy",       bg: "#d1fae5", text: "#065f46" },
    Medium:          { label: "Medium",     bg: "#dbeafe", text: "#1e40af" },
    Hard:            { label: "Hard",       bg: "#fee2e2", text: "#991b1b" },
  };
  const cfg = map[difficulty] || { label: difficulty || "—", bg: "#f3f4f6", text: "#374151" };
  return (
    <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold"
      style={{ background: cfg.bg, color: cfg.text }}>
      {cfg.label}
    </span>
  );
}

// ── ValidationChip ─────────────────────────────────────────────────────────

function ValidationChip({ passed, loading }) {
  if (loading) return (
    <span className="flex items-center gap-1 text-xs text-blue-600 font-medium">
      <span className="inline-block w-3 h-3 border border-blue-600 border-t-transparent rounded-full"
        style={{ animation: "spin 0.8s linear infinite" }} />
      Validating...
    </span>
  );
  if (passed === null) return null;
  return passed ? (
    <span className="flex items-center gap-1 text-xs text-emerald-700 font-semibold">
      <CheckCircle size={13} /> Passed
    </span>
  ) : (
    <span className="flex items-center gap-1 text-xs text-red-600 font-semibold">
      <XCircle size={13} /> Failed
    </span>
  );
}

// ── QuestionCard ───────────────────────────────────────────────────────────

function QuestionCard({ q, index, poolType, validResult, approved, onApprove, onDiscard, onRegenerate, isRegenerating }) {
  const [showExpl, setShowExpl] = useState(false);
  const passed  = validResult?.passed;
  const errors  = validResult?.errors || [];
  const isWarmUp = poolType === "WarmUp";
  const showLabels = !isWarmUp;

  const opts = q.options || {};
  const sortedEntries = Object.entries(opts).sort();

  return (
    <div
      className="rounded-xl border mb-4 overflow-hidden transition-all"
      style={{
        borderColor: passed === false ? "#fca5a5" : approved ? "#6ee7b7" : "#e5e7eb",
      }}
      data-testid={`question-card-${index}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3"
        style={{ background: passed === false ? "#fff1f2" : approved ? "#f0fdf4" : "#f9fafb" }}>
        <div className="flex items-center gap-2.5 flex-wrap">
          <span className="text-sm font-bold text-gray-500">Q{index + 1}</span>
          {q.difficulty && <DifficultyBadge difficulty={q.difficulty} />}
          <ValidationChip passed={validResult ? passed : null} loading={isRegenerating} />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {passed === false ? (
            <>
              <button
                onClick={onRegenerate}
                disabled={isRegenerating}
                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
                style={{ background: "#dbeafe", color: "#1e40af", opacity: isRegenerating ? 0.5 : 1 }}
                data-testid={`regenerate-btn-${index}`}
              >
                <RefreshCw size={12} /> Regenerate
              </button>
              <button
                onClick={onDiscard}
                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg"
                style={{ background: "#fee2e2", color: "#991b1b" }}
                data-testid={`discard-btn-${index}`}
              >
                <XCircle size={12} /> Discard
              </button>
            </>
          ) : (
            <button
              onClick={approved ? onDiscard : onApprove}
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors"
              style={approved
                ? { background: "#d1fae5", color: "#065f46" }
                : { background: "#f3f4f6", color: "#6b7280" }}
              data-testid={`approve-toggle-${index}`}
            >
              {approved ? <><CheckCircle size={12} /> Approved</> : <><XCircle size={12} /> Discard</>}
            </button>
          )}
        </div>
      </div>

      {/* Validation errors */}
      {errors.length > 0 && (
        <div className="mx-5 mt-3 bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-xs font-semibold text-red-700 mb-1 flex items-center gap-1.5">
            <AlertTriangle size={12} /> Validation failed:
          </p>
          <ul className="space-y-0.5">
            {errors.map((e, i) => (
              <li key={i} className="text-xs text-red-600">• {e}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Question text */}
      <div className="px-5 pt-4 pb-3">
        <p className="text-sm font-semibold text-gray-900 leading-snug">{q.question_text}</p>
      </div>

      {/* Options — always dict format */}
      <div className="px-5 pb-4 space-y-2">
        {sortedEntries.map(([letter, opt]) => {
          const label    = showLabels ? q.option_labels?.[letter] : null;
          const isCorrect = letter === q.correct_answer;
          return (
            <div key={letter}
              className="flex items-start gap-3 p-3 rounded-lg"
              style={{
                background: isCorrect ? "#f0fdf4" : "#f9fafb",
                border: `1px solid ${isCorrect ? "#6ee7b7" : "#e5e7eb"}`,
              }}
            >
              <span
                className="flex-shrink-0 w-6 h-6 rounded-full text-xs font-bold flex items-center justify-center mt-0.5"
                style={{ background: isCorrect ? "#10b981" : "#e5e7eb", color: isCorrect ? "white" : "#6b7280" }}
              >
                {letter}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center flex-wrap gap-1">
                  <span className="text-sm text-gray-800">{opt}</span>
                  {isCorrect ? (
                    <span className="text-xs px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 font-semibold">Correct</span>
                  ) : (
                    showLabels && label?.error_type && <ErrorTypeBadge type={label.error_type} />
                  )}
                </div>
                {showLabels && label?.note && (
                  <p className="text-xs text-gray-400 italic mt-0.5 leading-tight">{label.note}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Explanation */}
      <div className="px-5 pb-4">
        <button onClick={() => setShowExpl(v => !v)}
          className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600">
          <ChevronDown size={13} style={{ transform: showExpl ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s" }} />
          {showExpl ? "Hide" : "Show"} explanation
        </button>
        {showExpl && (
          <p className="text-xs text-gray-600 mt-2 leading-relaxed bg-gray-50 p-3 rounded-lg border border-gray-200">
            {q.explanation}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Label helpers ──────────────────────────────────────────────────────────

const POOL_LABELS = {
  WarmUp:    { badge: "WarmUp",  color: "#2563eb", light: "#dbeafe" },
  "Pool A":  { badge: "Pool A",  color: "#7c3aed", light: "#ede9fe" },
  "Pool B":  { badge: "Pool B",  color: "#b45309", light: "#fef3c7" },
  "Cold Pool": { badge: "Cold Pool", color: "#059669", light: "#d1fae5" },
};

// ── Main component ─────────────────────────────────────────────────────────

export default function UniversalGenerateModal({ row, poolType, token, onClose, onStored }) {
  const pl = POOL_LABELS[poolType] || POOL_LABELS["Pool A"];

  // Compute how many questions to generate based on current counts vs floor/target
  const computeInitialCount = () => {
    if (poolType === "WarmUp") {
      const gradeNum = parseInt((row.grade || "Grade 5").split(" ").pop()) || 5;
      const floor = gradeNum >= 5 ? 3 : 2;
      return Math.max(1, Math.min(10, floor - (row.warmup || 0)));
    }
    if (poolType === "Pool A") {
      return Math.max(1, Math.min(10, 5 - (row.pool_a || 0)));
    }
    if (poolType === "Pool B") {
      return Math.max(1, Math.min(10, 5 - (row.pool_b || 0)));
    }
    return 5;
  };
  const initialCount = computeInitialCount();

  // stages: generating | validating | preview | regenerating | storing | done | error
  const [stage,       setStage]       = useState("generating");
  const [questions,   setQuestions]   = useState([]);
  const [validations, setValidations] = useState([]); // [{index, passed, errors}]
  const [approved,    setApproved]    = useState({}); // {index: bool}
  const [discarded,   setDiscarded]   = useState({}); // {index: bool}
  const [regenIdx,    setRegenIdx]    = useState(null);
  const [errorMsg,    setErrorMsg]    = useState("");
  const [budgetError, setBudgetError] = useState(null); // {current_cost, max_budget, required_topup}
  const [storeResult, setStoreResult] = useState(null);

  // Escape key to close when not mid-generation/store
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape" && !["generating", "validating", "storing"].includes(stage)) {
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [stage, onClose]);

  const doGenerate = useCallback(async (count = 5, existingTexts = []) => {
    setStage("generating");
    setErrorMsg("");
    setBudgetError(null);
    try {
      const res = await apiFetch("/admin/cr/generate-pool", token, {
        method: "POST",
        body: JSON.stringify({
          grade:          row.grade,
          subject:        row.subject,
          topic:          row.topic,
          sub_topic:      row.sub_topic,
          pool_type:      poolType,
          count,
          existing_texts: existingTexts,
        }),
      });
      let data = {}; try { data = await res.json(); } catch (_) {}
      if (res.status === 402 && data.detail?.error_type === "budget_exceeded") {
        setBudgetError(data.detail);
        setStage("error");
        return null;
      }
      if (!res.ok) throw new Error(data.detail || "Generation failed");
      return data.questions || [];
    } catch (err) {
      setErrorMsg(err.message || "Generation failed");
      setStage("error");
      return null;
    }
  }, [row, poolType, token]);

  const doValidate = useCallback(async (qs) => {
    setStage("validating");
    try {
      const res = await apiFetch("/admin/cr/validate-generated", token, {
        method: "POST",
        body: JSON.stringify({ questions: qs, pool_type: poolType, subject: row.subject, grade: row.grade }),
      });
      let data = {}; try { data = await res.json(); } catch (_) {}
      if (!res.ok) throw new Error(data.detail || "Validation failed");
      return data.results || [];
    } catch (err) {
      setErrorMsg(err.message || "Validation failed");
      setStage("error");
      return null;
    }
  }, [row, poolType, token]);

  // Initial generation + validation
  useEffect(() => {
    (async () => {
      const qs = await doGenerate(initialCount);
      if (!qs) return;
      const results = await doValidate(qs);
      if (!results) return;

      setQuestions(qs);
      setValidations(results);

      // Default: approve all passed, discard all failed
      const ap = {}, dc = {};
      results.forEach(r => {
        if (r.passed) ap[r.index] = true;
        else dc[r.index] = true;
      });
      setApproved(ap);
      setDiscarded(dc);
      setStage("preview");
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Individual regeneration ───────────────────────────────────────────────

  const handleRegenerate = async (idx) => {
    setRegenIdx(idx);
    // Pass all current question texts so Claude avoids duplicating them
    const existingTexts = questions.map(q => q.question_text).filter(Boolean);
    const newQs = await doGenerate(1, existingTexts);
    if (!newQs || newQs.length === 0) { setRegenIdx(null); return; }

    const newQ = newQs[0];
    // Validate the single new question
    try {
      const res = await apiFetch("/admin/cr/validate-generated", token, {
        method: "POST",
        body: JSON.stringify({ questions: [newQ], pool_type: poolType, subject: row.subject, grade: row.grade }),
      });
      let data = {}; try { data = await res.json(); } catch (_) {}
      if (!res.ok) throw new Error(data.detail || "Validation failed");
      const newResult = (data.results || [{ index: 0, passed: false, errors: ["Validation error"] }])[0];

      setQuestions(prev => { const n = [...prev]; n[idx] = newQ; return n; });
      setValidations(prev => { const n = [...prev]; n[idx] = { ...newResult, index: idx }; return n; });
      if (newResult.passed) {
        setApproved(prev => ({ ...prev, [idx]: true }));
        setDiscarded(prev => { const n = { ...prev }; delete n[idx]; return n; });
      } else {
        setDiscarded(prev => ({ ...prev, [idx]: true }));
        setApproved(prev => { const n = { ...prev }; delete n[idx]; return n; });
      }
    } catch (_) {}

    setRegenIdx(null);
    setStage("preview");
  };

  // ── Actions ───────────────────────────────────────────────────────────────

  const handleApprove  = (i) => { setApproved(p => ({ ...p, [i]: true  })); setDiscarded(p => { const n={...p}; delete n[i]; return n; }); };
  const handleDiscard  = (i) => { setDiscarded(p => ({ ...p, [i]: true })); setApproved(p => { const n={...p}; delete n[i]; return n; }); };
  const approveAllPassed = () => {
    const ap = {}, dc = {};
    validations.forEach(r => {
      if (r.passed) ap[r.index] = true;
      else dc[r.index] = true;
    });
    setApproved(ap);
    setDiscarded(dc);
  };

  const toStore = questions.filter((_, i) => {
    const vr = validations.find(v => v.index === i);
    return approved[i] && vr?.passed;
  });

  const doStore = async () => {
    if (toStore.length === 0) return;
    setStage("storing");
    try {
      const res = await apiFetch("/admin/cr/store-pool", token, {
        method: "POST",
        body: JSON.stringify({
          questions: toStore,
          pool_type: poolType,
          grade:     row.grade,
          subject:   row.subject,
          sub_topic: row.sub_topic,
        }),
      });
      let data = {}; try { data = await res.json(); } catch (_) {}
      if (!res.ok) throw new Error(data.detail || "Store failed");
      setStoreResult(data);
      setStage("done");
      onStored(data.stored || toStore.length);
    } catch (err) {
      setErrorMsg(err.message || "Store failed");
      setStage("error");
    }
  };

  // ── Render helpers ────────────────────────────────────────────────────────

  const passedCount   = validations.filter(v => v.passed).length;
  const approvedCount = toStore.length;

  // ── Stage: loading spinner ────────────────────────────────────────────────
  const renderLoading = (msg, submsg) => (
    <div className="bg-white rounded-2xl p-10 max-w-sm w-full text-center shadow-2xl">
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div className="w-10 h-10 rounded-full border-2 border-t-transparent mx-auto mb-5"
        style={{ borderColor: pl.color, borderTopColor: "transparent", animation: "spin 0.8s linear infinite" }} />
      <h3 className="font-bold text-gray-900 mb-2">{msg}</h3>
      <p className="text-sm text-gray-500 mb-3">{submsg}</p>
      <p className="text-sm font-semibold px-4 py-2 rounded-lg" style={{ background: pl.light, color: pl.color }}>
        {row.sub_topic}
      </p>
      <p className="text-xs text-gray-400 mt-4">{row.grade} · {row.subject}</p>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.7)" }}
      data-testid="universal-generate-modal">
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Generating */}
      {stage === "generating" && renderLoading(
        `Generating ${poolType} questions via Claude…`,
        `${initialCount} NCERT-scoped MCQ${initialCount !== 1 ? "s" : ""} for:`
      )}

      {/* Validating */}
      {stage === "validating" && renderLoading(
        "Validating against AdioraSchema v4.3…",
        "Running 17 compliance checks…"
      )}

      {/* Storing */}
      {stage === "storing" && renderLoading(
        `Saving ${approvedCount} question${approvedCount !== 1 ? "s" : ""}…`,
        "Writing to fuel_questions"
      )}

      {/* Done */}
      {stage === "done" && (
        <div className="bg-white rounded-2xl p-8 max-w-sm w-full text-center shadow-2xl">
          <CheckCircle size={36} className="mx-auto mb-4 text-emerald-500" />
          <h3 className="font-bold text-gray-900 mb-2">Questions Stored</h3>
          <p className="text-sm text-gray-500">
            {storeResult?.stored || 0} {poolType} question{(storeResult?.stored || 0) !== 1 ? "s" : ""} saved for <strong>{row.sub_topic}</strong>
          </p>
          <p className="text-xs text-gray-400 mt-1">{row.grade} · {row.subject}</p>
          {storeResult?.errors?.length > 0 && (
            <div className="mt-3 text-left bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs font-semibold text-amber-700 mb-1">Skipped ({storeResult.errors.length}):</p>
              {storeResult.errors.slice(0, 3).map((e, i) => (
                <p key={i} className="text-xs text-amber-600">• {e}</p>
              ))}
            </div>
          )}
          <button onClick={onClose}
            className="mt-5 px-6 py-2.5 rounded-lg text-sm font-semibold text-white w-full"
            style={{ background: pl.color }}>
            Done
          </button>
        </div>
      )}

      {/* Error */}
      {stage === "error" && (
        <div className="bg-white rounded-2xl p-8 max-w-sm w-full shadow-2xl">
          {budgetError ? (
            <>
              <div className="flex items-start gap-3 mb-5">
                <div className="w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center"
                  style={{ background: "#fef3c7" }}>
                  <span style={{ fontSize: 20 }}>💳</span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">LLM Budget Exhausted</h3>
                  <p className="text-sm text-amber-700 mt-1">{budgetError.message}</p>
                </div>
              </div>
              <div className="rounded-xl p-4 mb-5 space-y-2 text-sm"
                style={{ background: "#fffbeb", border: "1px solid #fde68a" }}>
                <div className="flex justify-between">
                  <span className="text-gray-500">Current spend</span>
                  <span className="font-bold text-red-600">
                    ${budgetError.current_cost != null ? budgetError.current_cost.toFixed(4) : "—"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Budget limit</span>
                  <span className="font-semibold text-gray-700">
                    ${budgetError.max_budget != null ? budgetError.max_budget.toFixed(4) : "—"}
                  </span>
                </div>
                <div className="border-t pt-2 flex justify-between" style={{ borderColor: "#fde68a" }}>
                  <span className="font-semibold text-gray-700">Recommended top-up</span>
                  <span className="font-bold text-emerald-700">
                    +${budgetError.required_topup != null ? budgetError.required_topup.toFixed(2) : "—"}
                  </span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mb-5 leading-relaxed">
                Go to <strong>Profile → Universal Key → Add Balance</strong> (or enable auto top-up), then retry.
              </p>
            </>
          ) : (
            <div className="flex items-start gap-3 mb-5">
              <div className="w-10 h-10 flex-shrink-0 bg-red-100 rounded-full flex items-center justify-center">
                <XCircle size={20} className="text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Failed</h3>
                <p className="text-sm text-red-600 mt-1">{errorMsg}</p>
              </div>
            </div>
          )}
          <div className="flex gap-3">
            <button onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button onClick={() => {
              setStage("generating");
              (async () => {
                const qs = await doGenerate(initialCount);
                if (!qs) return;
                const results = await doValidate(qs);
                if (!results) return;
                setQuestions(qs);
                setValidations(results);
                const ap = {}, dc = {};
                results.forEach(r => { if (r.passed) ap[r.index] = true; else dc[r.index] = true; });
                setApproved(ap); setDiscarded(dc);
                setStage("preview");
              })();
            }}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white"
              style={{ background: pl.color }}
              data-testid="retry-generate-btn">
              <RefreshCw size={14} /> Retry
            </button>
          </div>
        </div>
      )}

      {/* Preview */}
      {(stage === "preview" || stage === "regenerating") && (
        <div className="bg-white rounded-2xl shadow-2xl flex flex-col"
          style={{ width: "100%", maxWidth: 740, maxHeight: "92vh" }}
          data-testid="universal-preview">

          {/* Modal header */}
          <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
            style={{ borderBottom: "1px solid #e5e7eb" }}>
            <div>
              <div className="flex items-center gap-2.5">
                <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-bold"
                  style={{ background: pl.light, color: pl.color }}>
                  {pl.badge}
                </span>
                <h2 className="font-bold text-gray-900">{poolType} Preview</h2>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">{row.sub_topic} · {row.grade} · {row.subject}</p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
              <X size={18} />
            </button>
          </div>

          {/* Sub-header: stats + bulk actions */}
          <div className="flex items-center justify-between px-6 py-3 flex-shrink-0 gap-3"
            style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
            <div className="flex items-center gap-4 flex-wrap">
              <span className="text-sm text-gray-500">{questions.length} generated</span>
              <span className="text-sm font-semibold text-emerald-700">
                {passedCount} passed validation
              </span>
              <span className="text-sm font-semibold" style={{ color: pl.color }}>
                {approvedCount} queued to save
              </span>
            </div>
            <button
              onClick={approveAllPassed}
              className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg flex-shrink-0"
              style={{ background: pl.light, color: pl.color }}
              data-testid="approve-all-passed-btn"
            >
              <CheckCircle size={12} /> Approve All Passed
            </button>
          </div>

          {/* Questions */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            {questions.map((q, i) => {
              const vr = validations.find(v => v.index === i);
              return (
                <QuestionCard
                  key={i}
                  q={q}
                  index={i}
                  poolType={poolType}
                  validResult={vr}
                  approved={!!approved[i]}
                  onApprove={() => handleApprove(i)}
                  onDiscard={() => handleDiscard(i)}
                  onRegenerate={() => handleRegenerate(i)}
                  isRegenerating={regenIdx === i}
                />
              );
            })}
            {questions.length === 0 && (
              <p className="text-sm text-gray-400 text-center py-8">No questions generated.</p>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 flex-shrink-0"
            style={{ borderTop: "1px solid #e5e7eb" }}>
            <button onClick={onClose}
              className="px-5 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
              Cancel
            </button>
            <button
              onClick={doStore}
              disabled={approvedCount === 0}
              className="flex items-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold text-white transition-opacity"
              style={{
                background: approvedCount > 0 ? pl.color : "#d1d5db",
                cursor: approvedCount > 0 ? "pointer" : "not-allowed",
              }}
              data-testid="save-questions-btn"
            >
              <Zap size={14} />
              Save {approvedCount} Approved Question{approvedCount !== 1 ? "s" : ""}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
